<template>
    <inline-tab-panel name="sent-emails">
        <flex-container>
            <flex-container :gap="Size.sm" align-items="center">
                <text-heading>
                    Emails
                </text-heading>

                <chip-informative :color="'primary'">
                    {{ props.sentEmailCollection.total }}
                </chip-informative>
            </flex-container>

            <card-normal without-padding>
                <list-container>
                    <template #header>
                        <list-cell-header>
                            <template #title>
                                Verzonden
                            </template>
                        </list-cell-header>
                        <list-cell-header>
                            <template #title>
                                Geopend
                            </template>
                        </list-cell-header>
                        <list-cell-header>
                            <template #title>
                                Geklikt
                            </template>
                        </list-cell-header>
                        <list-cell-header>
                            <template #title>
                                Soort
                            </template>
                        </list-cell-header>
                        <list-cell-header>
                            <template #title>
                                Onderwerp mail
                            </template>
                        </list-cell-header>
                        <list-cell-header>
                            <template #title>
                                Flow
                            </template>
                        </list-cell-header>
                        <list-cell-header />
                    </template>

                    <template #content>
                        <list-row v-if="props.sentEmailCollection.data.value.length === 0">
                            <list-cell-text>
                                <template #content>
                                    Er zijn geen emails voor dit contact
                                </template>
                            </list-cell-text>
                        </list-row>
                        <list-row
                            v-for="(sentEmail, index) in props.sentEmailCollection.data.value"
                            :key="sentEmail.id.value"
                            :background-color="index % 2 ? 'gray-100' : 'white'"
                            @click="openEmailModal(sentEmail)"
                            @mouseenter="setCurrentEmailId(sentEmail.id)"
                            @focusin="setCurrentEmailId(sentEmail.id)"
                            @mouseleave="resetCurrentEmailId()"
                            @focusout="resetCurrentEmailId()"
                        >
                            <list-cell-text>
                                <template #content>
                                    {{ formatDate(sentEmail.email.sendAt) }}
                                </template>
                            </list-cell-text>
                            <list-cell-text>
                                <template
                                    v-if="sentEmail.openedAt"
                                    #content
                                >
                                    {{ formatDate(sentEmail.openedAt) }}
                                </template>
                                <template
                                    v-else
                                    #content
                                >
                                    Niet gemeten
                                </template>
                            </list-cell-text>
                            <list-cell-text>
                                <template
                                    v-if="sentEmail.openedAt"
                                    #content
                                >
                                    <svg-icon
                                        :icon="IconCheck"
                                        :color="'green-500'"
                                        :size="IconSize.sm"
                                        is-custom-icon
                                    />
                                </template>
                                <template
                                    v-else
                                    #content
                                >
                                    <svg-icon
                                        :icon="IconX"
                                        :color="'red-500'"
                                        :size="IconSize.sm"
                                        is-custom-icon
                                    />
                                </template>
                            </list-cell-text>
                            <list-cell-text>
                                <template #content>
                                    {{ sentEmail.phase.name }}
                                </template>
                            </list-cell-text>
                            <list-cell-text>
                                <template #content>
                                    {{ sentEmail.email.subject }}
                                </template>
                            </list-cell-text>
                            <list-cell-text>
                                <template #content>
                                    {{ sentEmail.lead.pipeline.name }}
                                </template>
                            </list-cell-text>
                            <list-cell>
                                <flex-container
                                    v-show="sentEmail.id.equals(currentEmailId)"
                                    height="full"
                                    align-content="center"
                                    justify-content="end"
                                    style="padding-right: 10px"
                                >
                                    <button-default>
                                        <svg-icon
                                            :icon="IconEye"
                                            :size="IconSize.md"
                                            :color="'primary'"
                                            is-custom-icon
                                        />
                                    </button-default>
                                </flex-container>
                            </list-cell>
                        </list-row>
                    </template>
                </list-container>
            </card-normal>
        </flex-container>

        <modal-dialog
            ref="emailBodyModal"
            description="Hier kan je de email inzien"
            label="Email inhoud"
            :size="5"
        >
            <text-heading>
                Email inhoud
            </text-heading>
            <horizontal-line />
            <!-- eslint-disable-next-line -->
            <div v-html="emailBody" />
        </modal-dialog>
    </inline-tab-panel>
</template>

<script setup lang="ts">
    import { type ExposedOverlay, IconSize, ListRow, Size } from '@keyvue/one-ui';
    import type { EmailId, SentFlowEmail } from '@keyvue/one-api-client';
    import type { ReactiveCollection } from '@/services/keyvue-api/composables/useClient';
    import { formatDate } from '@/shared/utils/date';
    import IconX from '@keyvue/one-awesome-icons/icons/regular/x.svg?component';
    import IconCheck from '@keyvue/one-awesome-icons/icons/regular/check.svg?component';
    import IconEye from '@keyvue/one-awesome-icons/icons/regular/eye.svg?component';
    import { ref } from 'vue';

    const props = defineProps<{
        sentEmailCollection: ReactiveCollection<SentFlowEmail>
    }>();

    const emailBodyModal = ref<ExposedOverlay>();
    const emailBody = ref('');
    const currentEmailId = ref<null | EmailId>(null);
    const currentThrottle = ref<undefined | number>(undefined);

    function setCurrentEmailId(id: EmailId) {
        clearTimeout(currentThrottle.value);
        currentThrottle.value = undefined;
        currentEmailId.value = id;
    }

    function resetCurrentEmailId() {
        currentThrottle.value = setTimeout(() => {
            currentEmailId.value = null;
        }, 50) as unknown as number;
    }

    function openEmailModal(email: SentFlowEmail) {
        emailBody.value = email.email.body;
        emailBodyModal.value?.openOverlay();
    }
</script>
